/* Annuaire des partenaires (mission 490271) : recherche, filtre par métier, affichage par paquets et carte.
   Les fiches sont écrites par le serveur (blocs « Partenaire ») ; sans ce script elles sont toutes visibles, les outils et la carte restent cachés.
   Une page peut porter plusieurs sections « Annuaire des partenaires » (50 fiches au plus chacune) : le script réunit toutes les fiches de la page,
   la barre d'outils et la carte sont celles de la première section, le bouton « Afficher plus » et le message vide ceux de la dernière.
   La carte (Leaflet, servi par les fichiers du thème) lit les fiches de la liste : attributs data-lat, data-lng, data-nom, data-ville, data-pays, data-site.
   Les outils marquent chaque fiche data-zs-trouve="1" (elle correspond à la recherche et au filtre) ou "0", et préviennent la carte par l'événement zs-annuaire-maj. */

const nettoyer = (texte) => String(texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
const AUTRES = '*autres';

class ZsAnnuaireOutils extends HTMLElement {
  connectedCallback() {
    this.cartes = [...document.querySelectorAll('[data-zs-part]')].map((el) => ({
      el,
      metier: (el.dataset.metier || '').trim(),
      texte: nettoyer(el.dataset.recherche),
    }));
    const fins = document.querySelectorAll('[data-zs-fin]');
    this.fin = fins[fins.length - 1] ?? null;
    this.compteur = this.fin?.querySelector('[data-zs-compteur]');
    this.vide = this.fin?.querySelector('[data-zs-vide]');
    this.plus = this.fin?.querySelector('[data-zs-plus]');
    this.recherche = this.querySelector('[data-zs-recherche]');
    this.filtres = this.querySelector('[data-zs-filtres]');
    this.parPage = Math.max(1, Number(this.dataset.parPage) || 12);
    this.seuil = Number(this.dataset.seuil) || 0;
    this.montre = this.parPage;
    this.metier = '';

    const avecOutils = this.seuil > 0 && this.cartes.length >= this.seuil;
    this.hidden = !avecOutils;
    if (avecOutils) this.#construireFiltres();
    this.recherche?.addEventListener('input', () => { this.montre = this.parPage; this.#afficher(); });
    this.plus?.addEventListener('click', this.#suite);
    if (this.fin) this.fin.hidden = false;
    this.#afficher();
  }

  #construireFiltres() {
    const metiers = [...new Set(this.cartes.map((c) => c.metier).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'fr'));
    const avecAutres = this.cartes.some((c) => !c.metier);
    if (metiers.length + (avecAutres ? 1 : 0) < 2 || !this.filtres) return;
    const bouton = (libelle, valeur) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = libelle;
      b.dataset.metier = valeur;
      b.setAttribute('aria-pressed', valeur === '' ? 'true' : 'false');
      b.addEventListener('click', () => {
        this.metier = valeur;
        this.montre = this.parPage;
        for (const autre of this.filtres.children) autre.setAttribute('aria-pressed', autre === b ? 'true' : 'false');
        this.#afficher();
      });
      return b;
    };
    this.filtres.replaceChildren(
      bouton(this.dataset.tous || 'Tous', ''),
      ...metiers.map((m) => bouton(m, m)),
      ...(avecAutres ? [bouton(this.dataset.autres || 'Autres', AUTRES)] : []),
    );
    this.filtres.hidden = false;
  }

  #correspond(c, termes) {
    const bonMetier = !this.metier || (this.metier === AUTRES ? !c.metier : c.metier === this.metier);
    return bonMetier && termes.every((t) => c.texte.includes(t));
  }

  #correspondantes() {
    const termes = nettoyer(this.recherche?.value).split(/\s+/).filter(Boolean);
    return this.cartes.filter((c) => this.#correspond(c, termes));
  }

  #afficher() {
    const bonnes = this.#correspondantes();
    const visibles = new Set(bonnes.slice(0, this.montre));
    for (const c of this.cartes) {
      c.el.hidden = !visibles.has(c);
      c.el.dataset.zsTrouve = bonnes.includes(c) ? '1' : '0';
    }
    if (this.compteur) {
      const modele = bonnes.length === 1 ? this.dataset.compteurUn : this.dataset.compteurN;
      this.compteur.textContent = !this.hidden && bonnes.length && modele ? modele.replace('{n}', String(bonnes.length)) : '';
    }
    if (this.vide) this.vide.hidden = bonnes.length > 0;
    if (this.plus) this.plus.hidden = bonnes.length <= this.montre;
    this.bonnes = bonnes;
    document.dispatchEvent(new CustomEvent('zs-annuaire-maj'));
  }

  #suite = () => {
    const premiere = this.bonnes[this.montre];
    this.montre += this.parPage;
    this.#afficher();
    premiere?.el.querySelector('a[href]:not([tabindex="-1"])')?.focus({ preventScroll: false });
  };
}

const charger = (balise, attributs) => new Promise((ok, ko) => {
  const el = document.createElement(balise);
  Object.assign(el, attributs);
  el.addEventListener('load', ok, { once: true });
  el.addEventListener('error', () => ko(new Error(`${attributs.src || attributs.href} : chargement impossible`)), { once: true });
  document.head.append(el);
});

const PIN = '<svg viewBox="0 0 32 40" aria-hidden="true" focusable="false"><path fill="currentColor" stroke="#fff" stroke-width="2" d="M16 1.5C8.3 1.5 2.5 7.4 2.5 14.8c0 9.6 11.4 22.3 12.8 23.8.4.4 1 .4 1.4 0 1.4-1.5 12.8-14.2 12.8-23.8C29.5 7.4 23.7 1.5 16 1.5Z"/><circle cx="16" cy="14.5" r="5" fill="#fff"/></svg>';

// Fiche saisie dans l'admin sans latitude ni longitude (data-geo-ville) : centre de la commune par geo.api.gouv.fr (la commune seule est envoyée),
// retenu dans le navigateur pour ne pas redemander à chaque visite. Échec ou commune inconnue : la fiche reste dans la liste, pas sur la carte.
const CLE_GEO = 'zs-geo-communes-v1';
const geocoderLesNouvelles = async () => {
  const fiches = [...document.querySelectorAll('[data-zs-part][data-geo-ville]:not([data-lat])')];
  if (!fiches.length) return;
  let cache = {};
  try { cache = JSON.parse(localStorage.getItem(CLE_GEO) || '{}'); } catch { cache = {}; }
  const villes = [...new Set(fiches.map((f) => f.dataset.geoVille))].filter((v) => !(v in cache));
  await Promise.all(villes.map(async (ville) => {
    try {
      const r = await fetch('https://geo.api.gouv.fr/communes?nom=' + encodeURIComponent(ville) + '&fields=centre&boost=population&limit=1', { signal: AbortSignal.timeout(5000) });
      const [commune] = r.ok ? await r.json() : [];
      const [lng, lat] = commune?.centre?.coordinates ?? [];
      if (Number.isFinite(lat) && Number.isFinite(lng)) cache[ville] = [lat, lng];
    } catch { /* la fiche reste hors carte */ }
  }));
  try { localStorage.setItem(CLE_GEO, JSON.stringify(cache)); } catch { /* stockage refusé */ }
  for (const f of fiches) {
    const point = cache[f.dataset.geoVille];
    if (point) { f.dataset.lat = point[0]; f.dataset.lng = point[1]; }
  }
};

class ZsAnnuaireCarte extends HTMLElement {
  connectedCallback() {
    geocoderLesNouvelles().catch(() => {}).then(() => this.#initialiser());
  }

  #initialiser() {
    this.fiches = [...document.querySelectorAll('[data-zs-part][data-lat][data-lng]')]
      .map((el) => ({ el, lat: Number.parseFloat(el.dataset.lat), lng: Number.parseFloat(el.dataset.lng) }))
      .filter((f) => Number.isFinite(f.lat) && Number.isFinite(f.lng) && Math.abs(f.lat) <= 90 && Math.abs(f.lng) <= 180);
    this.cadre = this.querySelector('[data-zs-carte-cadre]');
    if (!this.fiches.length || !this.cadre) return;
    this.hidden = false;
    // Lien « passer la carte » : le focus va à la liste, que la navigation par ancre le fasse ou non.
    const passer = this.querySelector('.zs-ann__passer');
    passer?.addEventListener('click', (e) => {
      const liste = document.getElementById((passer.getAttribute('href') || '').slice(1));
      if (!liste) return;
      e.preventDefault();
      liste.focus();
      liste.scrollIntoView({ block: 'start' });
    });
    this.reperes = new Map();
    document.addEventListener('zs-annuaire-maj', () => this.#maj());
    const lancer = () => this.#demarrer().catch(() => { this.hidden = true; });
    if ('IntersectionObserver' in window) {
      const guet = new IntersectionObserver((entrees) => {
        if (entrees.some((e) => e.isIntersecting)) { guet.disconnect(); lancer(); }
      }, { rootMargin: '400px' });
      guet.observe(this);
    } else lancer();
  }

  async #demarrer() {
    if (!document.querySelector('link[href="' + this.dataset.leafletCss + '"]')) await charger('link', { rel: 'stylesheet', href: this.dataset.leafletCss });
    if (!window.L) await charger('script', { src: this.dataset.leafletJs, async: false });
    if (!window.L?.markerClusterGroup) await charger('script', { src: this.dataset.leafletCluster, async: false });
    this.#construire();
    this.#maj(true);
  }

  #construire() {
    const L = window.L;
    const calme = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const carte = L.map(this.cadre, {
      zoomControl: false,
      attributionControl: false,
      scrollWheelZoom: false,
      minZoom: 2,
      zoomAnimation: !calme,
      fadeAnimation: !calme,
      markerZoomAnimation: !calme,
    });
    L.control.zoom({ zoomInTitle: this.dataset.zoomAvant || 'Zoom avant', zoomOutTitle: this.dataset.zoomArriere || 'Zoom arrière' }).addTo(carte);
    L.control.attribution({ prefix: '<a href="https://leafletjs.com" target="_blank" rel="noopener noreferrer">Leaflet</a>' }).addTo(carte);
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: `© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">${this.#texte(this.dataset.attribution || 'contributeurs OpenStreetMap')}</a>`,
    }).addTo(carte);
    // La molette ne zoome la carte qu'une fois la carte activée (clic ou clavier) : elle ne retient pas le défilement de la page.
    carte.on('focus click', () => carte.scrollWheelZoom.enable());
    carte.on('blur', () => carte.scrollWheelZoom.disable());
    this.cadre.addEventListener('mouseleave', () => carte.scrollWheelZoom.disable());

    const modeleGroupe = this.dataset.groupe || '{n}';
    this.groupe = L.markerClusterGroup({
      showCoverageOnHover: false,
      maxClusterRadius: 45,
      animate: !calme,
      iconCreateFunction: (cluster) => {
        const n = cluster.getChildCount();
        const icone = L.divIcon({ html: `<span class="zs-groupe" aria-hidden="true">${n}</span>`, className: 'zs-groupe-icone', iconSize: [42, 42] });
        const creer = icone.createIcon.bind(icone);
        icone.createIcon = (ancien) => {
          const el = creer(ancien);
          el.setAttribute('aria-label', modeleGroupe.replace('{n}', String(n)));
          return el;
        };
        return icone;
      },
    });
    carte.addLayer(this.groupe);
    this.carte = carte;

    // Échap ferme la fenêtre d'un repère même quand le focus est sur un repère ou dans la fenêtre (Leaflet ne l'entend que si la carte elle-même a le focus).
    this.cadre.addEventListener('keydown', (e) => { if (e.key === 'Escape' && carte._popup) carte.closePopup(); });
    // Le focus revient au repère quand la fenêtre qui l'avait se ferme.
    carte.on('popupclose', (e) => {
      const actif = document.activeElement;
      if (!actif || actif === document.body || e.popup.getElement()?.contains(actif)) e.popup._source?.getElement()?.focus({ preventScroll: true });
    });
    carte.on('popupopen', (e) => {
      const fenetre = e.popup.getElement();
      fenetre?.querySelector('.leaflet-popup-close-button')?.setAttribute('aria-label', this.dataset.fermer || 'Fermer');
      if (this.ouvertClavier) {
        this.ouvertClavier = false;
        (fenetre?.querySelector('a.zs-pop__site') ?? fenetre?.querySelector('.leaflet-popup-close-button'))?.focus();
      }
    });
  }

  #texte(t) {
    const d = document.createElement('span');
    d.textContent = t;
    return d.innerHTML;
  }

  #repere(fiche) {
    let repere = this.reperes.get(fiche.el);
    if (repere) return repere;
    const L = window.L;
    const d = fiche.el.dataset;
    const icone = L.divIcon({ html: `<span class="zs-pin">${PIN}</span>`, className: 'zs-pin-icone', iconSize: [32, 40], iconAnchor: [16, 38], popupAnchor: [0, -34] });
    repere = L.marker([fiche.lat, fiche.lng], { icon: icone, title: [d.nom, d.ville].filter(Boolean).join(', '), keyboard: true, riseOnHover: true });
    // Entrée (déjà gérée par Leaflet) et Espace ouvrent la fenêtre du repère ; ouvertClavier amène alors le focus dans la fenêtre.
    repere.on('click', () => { this.ouvertClavier = false; });
    repere.on('keypress', (e) => { if (e.originalEvent.keyCode === 13) this.ouvertClavier = true; });
    repere.on('keydown', (e) => {
      if (e.originalEvent.key !== ' ') return;
      e.originalEvent.preventDefault();
      this.ouvertClavier = true;
      repere.openPopup();
    });
    repere.bindPopup(() => this.#fenetre(fiche), { maxWidth: 280, minWidth: 190, autoPanPadding: [24, 24] });
    this.reperes.set(fiche.el, repere);
    return repere;
  }

  #fenetre(fiche) {
    const d = fiche.el.dataset;
    const noeud = (balise, classe, texte) => {
      const n = document.createElement(balise);
      n.className = classe;
      n.textContent = texte;
      return n;
    };
    const boite = noeud('div', 'zs-pop', '');
    boite.append(noeud('strong', 'zs-pop__nom', d.nom || ''));
    if (d.metier) boite.append(noeud('span', 'zs-pop__metier', d.metier));
    const lieu = [d.ville, d.pays].filter(Boolean).join(', ');
    if (lieu) boite.append(noeud('span', 'zs-pop__ville', lieu));
    if (/^https?:\/\//i.test(d.site || '')) {
      const lien = noeud('a', 'zs-pop__site', d.siteTexte || 'Visiter le site');
      lien.href = d.site;
      lien.target = '_blank';
      lien.rel = 'noopener noreferrer';
      const dit = noeud('span', 'visually-hidden', ` : ${d.nom || ''} (${this.dataset.nouvelOnglet || 'nouvel onglet'})`);
      lien.append(dit);
      boite.append(lien);
    }
    return boite;
  }

  #maj(cadrer = false) {
    if (!this.carte) return;
    const bonnes = this.fiches.filter((f) => f.el.dataset.zsTrouve !== '0');
    this.groupe.clearLayers();
    this.groupe.addLayers(bonnes.map((f) => this.#repere(f)));
    if (bonnes.length) {
      // La carte cadre les résultats ; quelques fiches lointaines (outre-mer, étranger) ne la dézooment pas sur le monde quand la moitié au moins est en France métropolitaine.
      const metro = bonnes.filter((f) => f.lat >= 41 && f.lat <= 51.5 && f.lng >= -5.5 && f.lng <= 9.7);
      const cible = metro.length && metro.length * 2 >= bonnes.length ? metro : bonnes;
      this.carte.fitBounds(window.L.latLngBounds(cible.map((f) => [f.lat, f.lng])), { padding: [36, 36], maxZoom: 11, animate: !cadrer });
    } else if (cadrer) {
      this.carte.setView([46.6, 2.4], 5);
    }
  }
}

if (!customElements.get('zs-annuaire-outils')) customElements.define('zs-annuaire-outils', ZsAnnuaireOutils);
if (!customElements.get('zs-annuaire-carte')) customElements.define('zs-annuaire-carte', ZsAnnuaireCarte);
